import React, { useState, useEffect } from 'react';
import { DatePicker, Button, Table, Select, Checkbox, Spin, message, Space, Tag } from 'antd';
import type { TableProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { dutyScheduleService, DutyScheduleData, VitriTruc, AssignedEmployee, Employee, SaveDutyScheduleRequest } from '../services/dutyScheduleService';

const { RangePicker } = DatePicker;

// Add type for modified assignments
interface ModifiedAssignments {
  [date: string]: {
    [vitriTrucId: string]: AssignedEmployee[];
  };
}

// Helper to check permissions
const hasPermission = (permission: string): boolean => {
    const storedPermissions = localStorage.getItem('permissions');
    if (!storedPermissions) return false;
    try {
        const permissionsList: string[] = JSON.parse(storedPermissions);
        return permissionsList.includes(permission);
    } catch (e) {
        console.error("Failed to parse permissions from localStorage", e);
        return false;
    }
};

const DutyScheduleTab: React.FC = () => {
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>([dayjs().startOf('week'), dayjs().endOf('week')]);
    const [scheduleData, setScheduleData] = useState<DutyScheduleData | null>(null);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(false);
    const [modifiedAssignments, setModifiedAssignments] = useState<ModifiedAssignments>({});
    const [dates, setDates] = useState<[Dayjs, Dayjs] | null>(null);
    const canApprove = hasPermission('ApproveSchedule');

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                setLoading(true);
                const empData = await dutyScheduleService.getAllEmployees();
                setEmployees(empData);
                if (dateRange) {
                    const schedule = await dutyScheduleService.getDutySchedule(dateRange[0], dateRange[1]);
                    setScheduleData(schedule);
                }
            } catch (error) {
                message.error('Failed to load initial data');
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, []);

    const handleLoadData = async () => {
        if (!dateRange) {
            message.warning('Please select a date range');
            return;
        }
        try {
            setLoading(true);
            const schedule = await dutyScheduleService.getDutySchedule(dateRange[0], dateRange[1]);
            setScheduleData(schedule);
            setModifiedAssignments({}); // Clear modifications on new data load
        } catch (error) {
            message.error('Failed to load schedule data');
        } finally {
            setLoading(false);
        }
    };

    const disabledDate = (current: Dayjs) => {
        // Disable dates more than 2 weeks in the future
        if (current > dayjs().add(14, 'days').endOf('day')) {
          return true;
        }
        // If a start date is selected, disable dates more than 10 days after it
        if (dates && dates.length) {
          const tooLate = dates[0] && current.diff(dates[0], 'days') >= 10;
          const tooEarly = dates[1] && dates[1].diff(current, 'days') >= 10;
          return !!tooEarly || !!tooLate;
        }
        return false;
      };

    const handleAssignmentChange = (vitriTrucId: number, date: string, employees: AssignedEmployee[]) => {
        const key = `${vitriTrucId}_${date}`;
        setScheduleData(prev => {
            if (!prev) return null;
            const newAssignments = { ...prev.assignments, [key]: employees };
            return { ...prev, assignments: newAssignments };
        });

        setModifiedAssignments((prev: ModifiedAssignments) => ({
            ...prev,
            [date]: {
                ...(prev[date] || {}),
                [vitriTrucId]: employees
            }
        }));
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            // Get all unique dates that have modifications
            const datesToSave = Object.keys(modifiedAssignments);

            for (const date of datesToSave) {
                const assignmentsForThisDate: { [vitriTrucId: string]: AssignedEmployee[] } = {};

                // 1. Get existing assignments for this date from scheduleData
                if (scheduleData && scheduleData.positions) {
                    scheduleData.positions.forEach(pos => {
                        const key = `${pos.vitriTrucId}_${date}`;
                        if (scheduleData.assignments[key]) {
                            assignmentsForThisDate[pos.vitriTrucId.toString()] = scheduleData.assignments[key];
                        }
                    });
                }

                // 2. Apply modifications for this date
                const currentModifiedAssignments = modifiedAssignments[date];
                for (const vitriTrucId in currentModifiedAssignments) {
                    assignmentsForThisDate[vitriTrucId] = currentModifiedAssignments[vitriTrucId];
                }

                const saveRequest: SaveDutyScheduleRequest = {
                    date: date,
                    assignments: Object.keys(assignmentsForThisDate).map(vitriTrucId => ({
                        vitriTrucId: parseInt(vitriTrucId),
                        employees: assignmentsForThisDate[vitriTrucId]
                    }))
                };
                await dutyScheduleService.saveDutySchedule(saveRequest);
            }
            message.success('Schedule saved successfully!');
            setModifiedAssignments({});
            handleLoadData(); // Reload data to reflect saved changes and clear modified state
        } catch (error) {
            message.error('Failed to save schedule');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (lichtrucId: number | null) => {
        if (!lichtrucId) {
            message.error("Cannot approve a schedule that hasn't been saved yet.");
            return;
        }
        try {
            setLoading(true);
            await dutyScheduleService.approveDutySchedule(lichtrucId);
            message.success('Schedule approved successfully!');
            handleLoadData(); // Refresh data
        } catch (error) {
            message.error('Failed to approve schedule');
        } finally {
            setLoading(false);
        }
    };

    const handleUnapprove = async (lichtrucId: number | null) => {
        if (!lichtrucId) {
            message.error("Cannot unapprove a schedule without a valid ID.");
            return;
        }
        try {
            setLoading(true);
            await dutyScheduleService.unapproveDutySchedule(lichtrucId);
            message.success('Schedule unapproved successfully!');
            handleLoadData(); // Refresh data
        } catch (error) {
            message.error('Failed to unapprove schedule');
        } finally {
            setLoading(false);
        }
    };

    const handleDateRangeChange = (val: [Dayjs, Dayjs] | null) => {
        setDateRange(val);
    };

    const onCalendarChange = (val: [Dayjs, Dayjs] | null) => {
        setDates(val);
    };

    const columns: TableProps<VitriTruc>['columns'] = [
        {
            title: 'Vị trí trực',
            dataIndex: 'moTa',
            key: 'moTa',
            fixed: 'left',
            width: 200,
        },
        ...(scheduleData?.dates.map(dateCol => ({
            title: () => (
                <div style={{ textAlign: 'center' }}>
                    <div>{dayjs(dateCol.date).format('ddd, DD/MM')}</div>
                    {dateCol.isApproved ? (
                        <Space>
                            <Tag color="green">Đã duyệt</Tag>
                            <Button size="small" onClick={() => handleUnapprove(dateCol.lichtrucId)} disabled={!canApprove}>
                                Hủy duyệt
                            </Button>
                        </Space>
                    ) : (
                        <Button size="small" onClick={() => handleApprove(dateCol.lichtrucId)} disabled={Object.keys(modifiedAssignments).length > 0 || !canApprove || dateCol.isApproved}>
                            Duyệt
                        </Button>
                    )}
                </div>
            ),
            dataIndex: 'vitriTrucId',
            key: dateCol.date,
            width: 250,
            render: (vitriTrucId: number) => {
                const dateStr = dayjs(dateCol.date).format('YYYY-MM-DD');
                const key = `${vitriTrucId}_${dateStr}`;
                const assigned = scheduleData.assignments[key] || [];

                return (
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: '100%' }}
                        placeholder="Chọn nhân viên"
                        value={assigned.map(e => e.maChamCong)}
                        onChange={(selectedMaccs: string[]) => {
                            const newAssigned = selectedMaccs.map(macc => {
                                const existing = assigned.find(e => e.maChamCong === macc);
                                if (existing) return existing;
                                const employee = employees.find(e => e.macc === macc);
                                return {
                                    maChamCong: macc,
                                    tenNV: employee?.tennv || 'Unknown',
                                    isKem: false
                                };
                            });
                            handleAssignmentChange(vitriTrucId, dateStr, newAssigned);
                        }}
                        options={employees.map(emp => ({
                            label: `${emp.tennv}${emp.tenkk ? ` (${emp.viettat || emp.tenkk})` : ''}`,
                            value: emp.macc
                        }))}
                        tagRender={(props) => {
                            const { value, closable, onClose } = props;
                            const employee = employees.find(e => e.macc === value);
                            const assignedEmployee = assigned.find(e => e.maChamCong === value);
                            const label = employee ? `${employee.tennv}${employee.tenkk ? ` (${employee.viettat || employee.tenkk})` : ''}` : 'Unknown';

                            const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
                                event.preventDefault();
                                event.stopPropagation();
                            };
                            return (
                                <Tag
                                    onMouseDown={onPreventMouseDown}
                                    closable={closable}
                                    onClose={onClose}
                                    style={{ marginRight: 3, whiteSpace: 'normal' }}
                                >
                                    {label}
                                    <Checkbox
                                        style={{ marginLeft: 5 }}
                                        checked={assignedEmployee?.isKem}
                                        onChange={(e) => {
                                            const newAssigned = assigned.map(emp => 
                                                emp.maChamCong === value ? { ...emp, isKem: e.target.checked } : emp
                                            );
                                            handleAssignmentChange(vitriTrucId, dateStr, newAssigned);
                                        }}
                                        disabled={dateCol.isApproved}
                                    >Trực kèm</Checkbox>
                                </Tag>
                            );
                        }}
                        disabled={dateCol.isApproved}
                    />
                );
            }
        })) || []),
    ];

    return (
        <div style={{ padding: 24 }}>
            <Space style={{ marginBottom: 16 }}>
                <RangePicker 
                    value={dateRange}
                    disabledDate={disabledDate}
                    onCalendarChange={(val) => onCalendarChange(val as [Dayjs, Dayjs] | null)}
                    onChange={(val) => handleDateRangeChange(val as [Dayjs, Dayjs] | null)}
                />
                <Button type="primary" onClick={handleLoadData}>Tải dữ liệu</Button>
                {Object.keys(modifiedAssignments).length > 0 && (
                    <Button type="primary" danger onClick={handleSave}>Lưu thay đổi</Button>
                )}
            </Space>
            <Spin spinning={loading}>
                <Table
                    columns={columns}
                    dataSource={scheduleData?.positions}
                    rowKey="vitriTrucId"
                    bordered
                    pagination={false}
                    scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
                />
            </Spin>
        </div>
    );
};

export default DutyScheduleTab;